import React, { useState, useMemo } from "react";
import {
  Table,
  Button,
  Card,
  Tag,
  Space,
  Typography,
  Spin,
  Alert,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Descriptions,
  Row,
  Col,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import {
  useGetCreditAccountByPaymentTypeQuery,
  useUpdateCreditAccountStatusMutation,
} from "../../../Store/Apis/CreditAccountApi";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { confirm } = Modal;

const CreditAccounts = () => {
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();

  // Fetch credit accounts data
  const { data, error, isLoading, refetch } =
    useGetCreditAccountByPaymentTypeQuery();
  const [updateCreditAccount, { isLoading: isUpdating }] =
    useUpdateCreditAccountStatusMutation();

  // Filter records that have status "yes" (pending approval)
  const pendingRecords = useMemo(() => {
    // The API returns data directly as an array, not wrapped in data property
    const records = Array.isArray(data) ? data : data?.data || [];

    // Filter for records that need approval (exceed_Limit = "Yes" or "yes")
    const filtered = records.filter((record) => {
      const exceedLimit = record.exceed_Limit?.toString().toLowerCase();
      return exceedLimit === "yes";
    });

    console.log("Filtered pending records:", filtered);
    return filtered;
  }, [data]);

  // Handle status update
  const handleStatusUpdate = async (record, newStatus) => {
    const updateData = {
      clientId: record.clientID || 0,
      newStatus: record.creditAccStatus || "Active",
      exceed_Limit: newStatus,
    };

    try {
      const result = await updateCreditAccount(updateData).unwrap();

      console.log("API returned with result:", result);

      // Check for successful response by looking at updatedStatus field
      if (result && result.updatedStatus) {
        message.success(`Credit account ${newStatus} successfully!`);
        refetch(); // Refresh the data
      } else {
        // If we get here, it means the API returned but without updatedStatus
        message.error(
          result.message ||
            `Failed to ${newStatus.toLowerCase()} credit account`
        );
      }
    } catch (error) {
      console.error("Update error:", error);
      message.error(
        `Error updating credit account: ${error.message || "Unknown error"}`
      );
    }
  };

  // Show confirmation dialog
  const showConfirm = (record, action) => {
    const isConfirm = action === "confirmed";
    const title = `${isConfirm ? "Confirm" : "Decline"} `;
    confirm({
      title: title,
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>
            Are you sure you want to <strong>{title.toLowerCase()}</strong> this
            credit account?
          </p>
          <p>
            <strong>Customer:</strong> {record.name || record.clientID}
          </p>
          <p>
            <strong>Credit Limit:</strong> R{" "}
            {record.creditLimit?.toLocaleString() || 0}
          </p>
        </div>
      ),
      okText: isConfirm ? "Confirm" : "Decline",
      okType: isConfirm ? "primary" : "danger",
      cancelText: "Cancel",
      onOk() {
        handleStatusUpdate(record, action);
      },
    });
  };

  // View details modal
  const showDetails = (record) => {
    setSelectedRecord(record);
    setIsModalVisible(true);
  };

  // Table columns
  const columns = [
    {
      title: "Client ID",
      dataIndex: "clientID",
      key: "clientID",
      sorter: (a, b) => a.clientID - b.clientID,
    },
    {
      title: "Customer Name",
      dataIndex: "name",
      key: "name",
      render: (text) => text || "N/A",
    },
    {
      title: "Credit Limit",
      dataIndex: "creditLimit",
      key: "creditLimit",
      render: (amount) => `R ${amount?.toLocaleString() || 0}`,
      sorter: (a, b) => (a.creditLimit || 0) - (b.creditLimit || 0),
    },
    {
      title: "Payment Terms",
      dataIndex: "paymentTerms",
      key: "paymentTerms",
      render: (text) => text || "N/A",
    },
    {
      title: "Status",
      dataIndex: "exceed_Limit",
      key: "status",
      render: (status) => (
        <Tag color="orange" icon={<ExclamationCircleOutlined />}>
          Pending Approval
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => showDetails(record)}
          >
            View
          </Button>
          <Button
            type="primary"
            size="small"
            icon={<CheckCircleOutlined />}
            onClick={() => showConfirm(record, "confirmed")}
            loading={isUpdating}
          >
            Confirm
          </Button>
          <Button
            danger
            size="small"
            icon={<CloseCircleOutlined />}
            onClick={() => showConfirm(record, "declined")}
            loading={isUpdating}
          >
            Decline
          </Button>
        </Space>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
        <p style={{ marginTop: 16 }}>Loading credit accounts...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Data"
        description={error.message || "Failed to load credit accounts"}
        type="error"
        showIcon
        action={
          <Button size="small" onClick={refetch}>
            Retry
          </Button>
        }
      />
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div style={{ marginBottom: 24 }}>
          <Title level={2}>Credit Account Management</Title>
          <Text type="secondary">
            Review and approve pending credit account requests
          </Text>
        </div>

        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col span={8}>
            <Card size="small">
              <div style={{ textAlign: "center" }}>
                <Title level={3} style={{ margin: 0, color: "#fa8c16" }}>
                  {pendingRecords.length}
                </Title>
                <Text type="secondary">Pending Approvals</Text>
              </div>
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small">
              <div style={{ textAlign: "center" }}>
                <Title level={3} style={{ margin: 0, color: "#52c41a" }}>
                  R{" "}
                  {pendingRecords
                    .reduce((sum, record) => sum + (record.creditLimit || 0), 0)
                    .toLocaleString()}
                </Title>
                <Text type="secondary">Total Credit Requested</Text>
              </div>
            </Card>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={pendingRecords}
          rowKey={(record) => record.clientID || record.id || Math.random()}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          scroll={{ x: 800 }}
        />

        {/* Details Modal */}
        <Modal
          title="Credit Account Details"
          open={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          footer={[
            <Button key="close" onClick={() => setIsModalVisible(false)}>
              Close
            </Button>,
            <Button
              key="confirm"
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={() => {
                showConfirm(selectedRecord, "confirmed");
                setIsModalVisible(false);
              }}
            >
              Confirm
            </Button>,
            <Button
              key="decline"
              danger
              icon={<CloseCircleOutlined />}
              onClick={() => {
                showConfirm(selectedRecord, "declined");
                setIsModalVisible(false);
              }}
            >
              Decline
            </Button>,
          ]}
          width={600}
        >
          {selectedRecord && (
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Client ID">
                {selectedRecord.clientID}
              </Descriptions.Item>
              <Descriptions.Item label="Customer Name">
                {selectedRecord.name || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Credit Limit">
                R {selectedRecord.creditLimit?.toLocaleString() || 0}
              </Descriptions.Item>
              <Descriptions.Item label="Payment Terms">
                {selectedRecord.paymentTerms || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Address">
                {selectedRecord.address || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Phone">
                {selectedRecord.phone || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {selectedRecord.email || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Client Type">
                {selectedRecord.clientType || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Payment Type">
                {selectedRecord.paymentType || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Credit Account Status">
                {selectedRecord.creditAccStatus || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Current Limit">
                R {selectedRecord.currentLimit?.toLocaleString() || 0}
              </Descriptions.Item>
              <Descriptions.Item label="Request Date">
                {selectedRecord.createdAt
                  ? new Date(selectedRecord.createdAt).toLocaleDateString()
                  : "N/A"}
              </Descriptions.Item>
            </Descriptions>
          )}
        </Modal>
      </Card>
    </div>
  );
};

export default CreditAccounts;
